import React, { useState, useEffect, useRef } from 'react';
import { X, AlertTriangle, AlertCircle } from 'lucide-react';
import { ProjectCard, Task } from '../../types/projects';
import { QuillEditor } from '../Editor/QuillEditor';
import 'react-quill/dist/quill.snow.css';

interface CardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { title: string; content: string; label: 'warning' | 'danger' | null; dueDate?: string; tasks?: Task[] }) => void;
  card: ProjectCard | null;
}

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
  ]
};

export const CardModal: React.FC<CardModalProps> = ({
  isOpen,
  onClose,
  onSave,
  card
}) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    label: null as 'warning' | 'danger' | null,
    dueDate: '',
    tasks: [] as Task[]
  });

  const editorRef = useRef(null);

  useEffect(() => {
    if (card) {
      setFormData({
        title: card.title,
        content: card.content,
        label: card.label || null,
        dueDate: card.dueDate || '',
        tasks: card.tasks || []
      });
    } else {
      setFormData({
        title: '',
        content: '',
        label: null,
        dueDate: '',
        tasks: []
      });
    }
  }, [card]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg w-full max-w-2xl p-6 m-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {card ? 'Modifier la carte' : 'Nouvelle carte'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Titre
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date limite
            </label>
            <input
              type="date"
              value={formData.dueDate}
              onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Étiquette
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setFormData({
                  ...formData,
                  label: formData.label === 'warning' ? null : 'warning'
                })}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                  formData.label === 'warning'
                    ? 'bg-orange-100 text-orange-600 border-orange-200 dark:bg-orange-900/20 dark:border-orange-500'
                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-orange-50 dark:hover:bg-orange-900/10'
                }`}
              >
                <AlertTriangle className="w-4 h-4" />
                Attention
              </button>
              <button
                type="button"
                onClick={() => setFormData({
                  ...formData,
                  label: formData.label === 'danger' ? null : 'danger'
                })}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                  formData.label === 'danger'
                    ? 'bg-red-100 text-red-600 border-red-200 dark:bg-red-900/20 dark:border-red-500'
                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-red-900/10'
                }`}
              >
                <AlertCircle className="w-4 h-4" />
                Urgent
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Contenu
            </label>
            <QuillEditor
              ref={editorRef}
              value={formData.content}
              onChange={(content) => setFormData({ ...formData, content })}
              modules={modules}
              className="bg-white dark:bg-gray-700 rounded-lg [&_.ql-editor]:min-h-[200px] [&_.ql-container]:text-gray-900 dark:[&_.ql-container]:text-white [&_.ql-toolbar]:border-gray-300 dark:[&_.ql-toolbar]:border-gray-600 [&_.ql-container]:border-gray-300 dark:[&_.ql-container]:border-gray-600"
            />
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Annuler
            </button>
            <button
              onClick={() => onSave(formData)}
              className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              {card ? 'Mettre à jour' : 'Ajouter'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};